import { useState, useEffect, useRef } from 'react';
import i18n from '../i18n/config';

interface UseVoiceInputOptions {
  language?: string;
  continuous?: boolean;
  onResult?: (transcript: string) => void;
  onError?: (error: string) => void;
}

export function useVoiceInput(options: UseVoiceInputOptions = {}) {
  const {
    language = 'ru-RU',
    continuous = false,
    onResult,
    onError,
  } = options;

  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [isSupported, setIsSupported] = useState(false);
  const recognitionRef = useRef<any>(null);
  // Whether the user still wants to dictate — Chrome auto-stops on silence even in continuous
  // mode, so we restart on `onend` while this is true to keep a long dictation alive.
  const wantListeningRef = useRef(false);

  // Keep the latest callbacks in refs so the recognition instance is created ONCE and
  // isn't torn down (aborting an in-progress dictation) on every parent re-render.
  const onResultRef = useRef(onResult);
  const onErrorRef = useRef(onError);
  onResultRef.current = onResult;
  onErrorRef.current = onError;

  useEffect(() => {
    // Check if Speech Recognition is supported
    const SpeechRecognition =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setIsSupported(false);
      return;
    }

    setIsSupported(true);

    // Initialize Speech Recognition
    const recognition = new SpeechRecognition();
    recognition.lang = language;
    recognition.continuous = continuous;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      console.log('🎤 Voice recognition started');
      setIsListening(true);
    };

    recognition.onresult = (event: any) => {
      let finalTranscript = '';
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const transcriptPiece = result[0].transcript;

        if (result.isFinal) {
          finalTranscript += transcriptPiece + ' ';
        } else {
          interimTranscript += transcriptPiece;
        }
      }

      const currentTranscript = finalTranscript || interimTranscript;
      setTranscript(currentTranscript.trim());

      if (finalTranscript && onResultRef.current) {
        onResultRef.current(finalTranscript.trim());
      }
    };

    recognition.onerror = (event: any) => {
      // 'no-speech' / 'aborted' are benign (silence gaps, restart churn) — onend will restart
      // if the user still wants to dictate; don't surface these as errors.
      if (event.error === 'no-speech' || event.error === 'aborted') return;
      console.error('Voice recognition error:', event.error);
      wantListeningRef.current = false;
      setIsListening(false);
      if (onErrorRef.current) onErrorRef.current(getErrorMessage(event.error));
    };

    recognition.onend = () => {
      // Keep dictating across Chrome's silence-triggered stops.
      if (wantListeningRef.current) {
        try { recognition.start(); return; } catch { /* fall through to stop */ }
      }
      setIsListening(false);
    };

    recognitionRef.current = recognition;

    return () => {
      wantListeningRef.current = false;
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, [language, continuous]);

  const startListening = () => {
    if (!recognitionRef.current) return;

    try {
      setTranscript('');
      wantListeningRef.current = true;
      recognitionRef.current.start();
    } catch (error) {
      // start() throws if already started — treat as already listening.
      console.error('Failed to start recognition:', error);
    }
  };

  const stopListening = () => {
    if (!recognitionRef.current) return;
    wantListeningRef.current = false;
    try {
      recognitionRef.current.stop();
    } catch (error) {
      console.error('Failed to stop recognition:', error);
    }
  };

  const resetTranscript = () => {
    setTranscript('');
  };

  return {
    isListening,
    transcript,
    isSupported,
    startListening,
    stopListening,
    resetTranscript,
  };
}

function getErrorMessage(error: string): string {
  const errorMessages: Record<string, string> = {
    'no-speech': i18n.t('voiceInput.noSpeech'),
    'audio-capture': i18n.t('voiceInput.audioCapture'),
    'not-allowed': i18n.t('voiceInput.notAllowed'),
    'network': i18n.t('voiceInput.network'),
    'aborted': i18n.t('voiceInput.aborted'),
  };

  return errorMessages[error] || i18n.t('voiceInput.recognitionError', { error });
}
